// apps/vendor/src/features/orders/LabelDetailModal.tsx
//
// Per-label detail modal opened from the orders list Label cell when a label
// already exists. Previews the label (PDF via <iframe>, image via <img>),
// with Print (sends to the printer) + Download (saves the PDF) actions.
//
// Distinct from LabelPrintModal (bulk label GENERATION + format picker). This
// is a single-label VIEWER for an already-bought label_url.

import { useRef } from 'react';

import { Button } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';

interface Props {
  open: boolean;
  labelUrl: string;
  trackingNumber?: string | null;
  onClose: () => void;
}

// Heuristic: treat .pdf (or no image extension) as PDF → iframe; image
// extensions → <img>. Both are previewable; print/download work for either.
function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp)(\?|#|$)/i.test(url);
}

export function LabelDetailModal({
  open,
  labelUrl,
  trackingNumber,
  onClose,
}: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  if (!open) return null;

  const isImage = isImageUrl(labelUrl);

  function handlePrint(): void {
    // Print the previewed label. For a same-origin PDF/image the hidden iframe
    // can print directly; otherwise open a print-focused window as fallback
    // (cross-origin frames block contentWindow.print()).
    const frame = frameRef.current;
    try {
      if (frame?.contentWindow) {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        return;
      }
    } catch {
      /* cross-origin → fall through to window-open fallback */
    }
    const w = window.open(labelUrl, '_blank');
    if (w) {
      w.addEventListener('load', () => {
        try {
          w.focus();
          w.print();
        } catch {
          /* best-effort */
        }
      });
    }
  }

  function handleDownload(): void {
    // Anchor with download attr. Cross-origin servers may ignore the filename
    // hint, but the click still triggers a download/navigation to the PDF.
    const a = document.createElement('a');
    a.href = labelUrl;
    a.download = trackingNumber ? `label-${trackingNumber}.pdf` : 'label.pdf';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="label-detail-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-pw-text">
            {t('labelDetailModal.title')}
          </h2>
          {trackingNumber ? (
            <span className="font-mono text-xs text-pw-text-muted">
              {trackingNumber}
            </span>
          ) : null}
        </div>

        {/* Preview — image inline, otherwise PDF in an iframe. */}
        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-pw-border bg-pw-surface">
          {isImage ? (
            <img
              src={labelUrl}
              alt={t('labelDetailModal.previewAlt')}
              data-testid="label-detail-image"
              className="mx-auto max-h-[60vh] w-auto object-contain"
            />
          ) : (
            <iframe
              ref={frameRef}
              src={labelUrl}
              title={t('labelDetailModal.previewAlt')}
              data-testid="label-detail-frame"
              className="h-[60vh] w-full"
            />
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="label-detail-close"
            onClick={onClose}
          >
            {t('labelDetailModal.close')}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="label-detail-download"
            onClick={handleDownload}
          >
            {t('labelDetailModal.download')}
          </Button>
          <Button
            type="button"
            data-testid="label-detail-print"
            onClick={handlePrint}
          >
            {t('labelDetailModal.print')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LabelDetailModal;
